import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Plus, Trash2, Link2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'
import { socialLinksApi } from '@/services/socialLinksApi'
import type { SocialLink } from '@/types'

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'pinterest', label: 'Pinterest' },
  { value: 'website', label: 'Website' },
] as const

export default function SocialLinksEditor() {
  const { t } = useTranslation('dashboard')
  const embedded = useDashboardEmbedded()
  const queryClient = useQueryClient()
  const [platform, setPlatform] = useState('instagram')
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: links = [], isLoading } = useQuery({
    queryKey: ['social-links'],
    queryFn: socialLinksApi.list,
  })

  const createMutation = useMutation({
    mutationFn: socialLinksApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-links'] })
      setUrl('')
      setLabel('')
      setError(null)
    },
    onError: () => setError(t('socialLinks.errors.saveFailed')),
  })

  const deleteMutation = useMutation({
    mutationFn: socialLinksApi.remove,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['social-links'] }),
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    if (!url.trim()) return
    setError(null)
    createMutation.mutate({
      platform,
      url: url.trim(),
      label: label.trim() || undefined,
    })
  }

  const platformLabel = (link: SocialLink) =>
    PLATFORMS.find((p) => p.value === link.platform)?.label ?? link.platform

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-8">
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('socialLinks.title')}</h1>
          <p className="text-gray-500">{t('socialLinks.subtitle')}</p>
        </div>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('socialLinks.add.title')}</CardTitle>
          <CardDescription>{t('socialLinks.add.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('socialLinks.add.platform')}
                </label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg bg-background"
                >
                  {PLATFORMS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('socialLinks.add.label')}
                </label>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={t('socialLinks.add.labelPlaceholder')}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('socialLinks.add.url')} *
              </label>
              <Input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://"
                required
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={createMutation.isPending || !url.trim()}>
              {createMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {t('socialLinks.add.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('socialLinks.list.title')}</CardTitle>
          <CardDescription>{t('socialLinks.list.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : links.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t('socialLinks.list.empty')}
            </p>
          ) : (
            <ul className="space-y-3">
              {links.map((link) => (
                <li
                  key={link.id}
                  className="flex items-center justify-between gap-3 rounded-lg border px-4 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Link2 className="w-4 h-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{platformLabel(link)}</p>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary truncate block hover:underline"
                      >
                        {link.label || link.url}
                      </a>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMutation.mutate(link.id)}
                    disabled={deleteMutation.isPending}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
